import React, { useState } from 'react';

import {
  QBox,
  QFormControl,
  QHeading,
  QSelect,
  QStack,
  QModal,
  QModalBody,
  QModalHeader,
  QText,
  QCloseButton,
  QModalActions,
  QButton,
  QAlert,
} from '@qualio/ui-components';
import { FormikProps } from 'formik';
import { useConfigsQuery } from 'hooks';
import { moveFilesToAdditionalDocuments } from 'lib/utils';
import { useTranslation } from 'react-i18next';
import { BufferedQInput, BufferedQTextarea } from 'v2components';
import { AddSupplierFormikPayload, ConfigTypeOptions } from 'v2types';

import { useSponsors } from '../../../../hooks';
import WarningMessage from '../WarningMessage/WarningMessage';

type GeneralInfoFormProps = {
  formik: FormikProps<AddSupplierFormikPayload>;
  isDraft: boolean;
  handleStepChange: (stepIndex: number) => void;
};

export const GeneralInfoForm: React.FC<GeneralInfoFormProps> = ({ formik, isDraft, handleStepChange }) => {
  const { t } = useTranslation();
  const { supplierTypeOptions, isConfigsLoading } = useConfigsQuery();
  const { usersOptions, isCompanyUsersLoading } = useSponsors();
  const [currentSupplierType, setCurrentSupplierType] = useState<ConfigTypeOptions>();
  const [showModal, setShowModal] = useState(false);

  const { handleChange, status, touched, errors, values, setFieldValue } = formik;
  const isFieldInvalid = (field: keyof AddSupplierFormikPayload): boolean => {
    return !!touched && !!touched[field] && !!errors[field];
  };

  const updateSupplierType = () => {
    setFieldValue('supplierType', currentSupplierType);
    setFieldValue('documents', moveFilesToAdditionalDocuments(values.documents));
    setFieldValue('supplierTypeOrRiskUpdated', true);
    setShowModal(false);
  };

  return (
    <QBox maxWidth="640px" mb={2}>
      <QHeading mb={6}>{t('addSupplier.generalInfo.heading')}</QHeading>
      <QBox>
        {isDraft && errors.contactEmail && !(status === 'touched' && !!touched) ? (
          <QAlert
            status="warning"
            title={t('addSupplier.generalInfo.warnings.invalidFields')}
            description={
              <QBox pt={2}>
                <QStack spacing={2}>
                  {Object.entries(errors) // Adding this to make future validations easier (if any)
                    .filter(([name]) => name === 'contactEmail')
                    .map(([name, error], index) => (
                      <WarningMessage
                        key={index}
                        name={name}
                        error={error as string}
                        handleStepChange={handleStepChange}
                      />
                    ))}
                </QStack>
              </QBox>
            }
          />
        ) : null}
      </QBox>
      <QStack spacing={4}>
        <QFormControl
          label={t('addSupplier.generalInfo.fields.supplierName.label')}
          isInvalid={isFieldInvalid('name')}
          error={errors?.name}
          helper={t('addSupplier.generalInfo.fields.supplierName.helper')}
        >
          <BufferedQInput
            data-cy="supplier-name"
            name="name"
            onChange={(e) => handleChange({ ...e, target: e.currentTarget })}
            value={values.name}
          />
        </QFormControl>
        <QFormControl
          data-cy="supplier-type"
          label={t('addSupplier.generalInfo.fields.supplierType.label')}
          isInvalid={isFieldInvalid('supplierType')}
          error={errors.supplierType as string}
          helper={t('addSupplier.generalInfo.fields.supplierType.helper')}
        >
          <QSelect
            name="supplierType"
            onChange={(e: ConfigTypeOptions | null) => {
              if (values.documents.length) {
                if (e === null) {
                  return;
                }
                if (values.documents.length > 1 && values.documents[0].files.length) {
                  setCurrentSupplierType(e);
                  setShowModal(true);
                } else {
                  setFieldValue('supplierType', e);
                }
              } else {
                setFieldValue('supplierType', e);
              }
            }}
            value={values.supplierType?.value}
            isLoading={isConfigsLoading}
            options={supplierTypeOptions}
          />
        </QFormControl>
        <QFormControl label={t('addSupplier.generalInfo.fields.website.label')} isInvalid={isFieldInvalid('website')}>
          <BufferedQInput data-cy="supplier-website" name="website" onChange={handleChange} value={values.website} />
        </QFormControl>
        <QFormControl
          label={t('addSupplier.generalInfo.fields.intendedUse.label')}
          isInvalid={isFieldInvalid('intendedUse')}
          error={errors?.intendedUse}
        >
          <BufferedQTextarea
            data-cy="intended-use"
            name="intendedUse"
            onChange={(e) => handleChange({ ...e, target: e.currentTarget })}
            value={values.intendedUse}
          />
        </QFormControl>
        <QFormControl
          data-cy="supplier-owner"
          label={t('addSupplier.generalInfo.fields.sponsor.label')}
          isInvalid={isFieldInvalid('sponsor')}
          error={errors.sponsor as string}
          helper={t('addSupplier.generalInfo.fields.sponsor.helper')}
        >
          <QSelect
            name="sponsor"
            onChange={(e: ConfigTypeOptions | null) => {
              if (e === null) {
                return;
              }
              setFieldValue('sponsor', e);
            }}
            value={values.sponsor?.value}
            options={usersOptions}
            isLoading={isCompanyUsersLoading}
          />
        </QFormControl>
      </QStack>
      <QHeading size="sm" mt={6} mb={4}>
        {t('addSupplier.generalInfo.contactInfo.heading')}
      </QHeading>
      <QStack spacing={4}>
        <QFormControl
          label={t('addSupplier.generalInfo.contactInfo.fields.name')}
          isInvalid={isFieldInvalid('contactName')}
        >
          <BufferedQInput
            data-cy="supplier-contact-name"
            name="contactName"
            onChange={handleChange}
            value={values.contactName}
          />
        </QFormControl>
        <QFormControl
          label={t('addSupplier.generalInfo.contactInfo.fields.email')}
          isInvalid={isFieldInvalid('contactEmail')}
        >
          <BufferedQInput
            data-cy="supplier-contact-email"
            name="contactEmail"
            onChange={handleChange}
            value={values.contactEmail}
          />
        </QFormControl>
        <QFormControl
          label={t('addSupplier.generalInfo.contactInfo.fields.phone')}
          isInvalid={isFieldInvalid('contactPhone')}
        >
          <BufferedQInput
            data-cy="supplier-contact-phone"
            name="contactPhone"
            onChange={handleChange}
            value={values.contactPhone}
          />
        </QFormControl>
        <QFormControl
          label={t('addSupplier.generalInfo.contactInfo.fields.address')}
          isInvalid={isFieldInvalid('contactAddress')}
        >
          <BufferedQInput
            data-cy="supplier-contact-address"
            name="contactAddress"
            onChange={handleChange}
            value={values.contactAddress}
          />
        </QFormControl>
        <QFormControl
          label={t('addSupplier.generalInfo.contactInfo.fields.notes')}
          isInvalid={isFieldInvalid('contactNotes')}
          error={errors?.contactNotes}
        >
          <BufferedQTextarea
            data-cy="supplier-contact-notes"
            name="contactNotes"
            onChange={(evt) => handleChange({ ...evt, target: evt.currentTarget })}
            value={values.contactNotes}
          />
        </QFormControl>
      </QStack>
      <QModal onClose={() => setShowModal(false)} size="md" isOpen={showModal}>
        <QModalHeader>
          <QText>{t('addSupplier.modals.changeSupplierType.title')}</QText>
          <QCloseButton onClick={() => setShowModal(false)} />
        </QModalHeader>
        <QModalBody>
          <div data-cy="change-supplier-level-modal-text">{t('addSupplier.modals.changeSupplierType.message')}</div>
        </QModalBody>
        <QModalActions>
          <QButton
            isDestructive
            onClick={() => setShowModal(false)}
            variant="outline"
            data-cy="cancel-supplier-type-change"
          >
            {t('addSupplier.modals.changeSupplierType.buttons.cancel')}
          </QButton>
          <QButton onClick={updateSupplierType}>{t('addSupplier.modals.changeSupplierType.buttons.change')}</QButton>
        </QModalActions>
      </QModal>
    </QBox>
  );
};
